import { useCallback, useEffect, useRef } from "react";

import { AuctionSocketEvent } from "@simplyk/common";
import socketIOClient from "socket.io-client";

const DEBOUNCE_MS = 200;

const SOCKET_OPTIONS = {
  secure: true,
  reconnect: true,
  rejectUnauthorized: false,
  transports: ["websocket"] as "websocket"[],
};

interface BidsUpdatedPayload {
  ticketingId: string;
  version: string;
}

interface UseAuctionBidsSocketParams {
  ticketingId: string | null;
  onUpdated: (version: string) => void;
}

export const useAuctionBidsSocket = ({ ticketingId, onUpdated }: UseAuctionBidsSocketParams): void => {
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingVersionRef = useRef<string | null>(null);

  const debouncedOnUpdated = useCallback((version: string) => {
    pendingVersionRef.current = version;
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const v = pendingVersionRef.current;
      pendingVersionRef.current = null;
      if (v !== null) {
        onUpdatedRef.current(v);
      }
    }, DEBOUNCE_MS);
  }, []);

  const joinRoom = useCallback(
    (socket: ReturnType<typeof socketIOClient>) => {
      if (ticketingId) {
        socket.emit(AuctionSocketEvent.JoinRoom, { ticketingId });
      }
    },
    [ticketingId]
  );

  useEffect(() => {
    if (!ticketingId) {
      return;
    }

    const socket = socketIOClient(process.env.NEXT_PUBLIC_SOCKET_SERVER_URL, SOCKET_OPTIONS);

    joinRoom(socket);

    socket.on(AuctionSocketEvent.BidsUpdated, (payload: BidsUpdatedPayload) => {
      debouncedOnUpdated(payload.version);
    });

    socket.io.on("reconnect", () => {
      joinRoom(socket);
      debouncedOnUpdated(`reconnect-${Date.now()}`);
    });

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      socket.emit(AuctionSocketEvent.LeaveRoom, { ticketingId });
      socket.off(AuctionSocketEvent.BidsUpdated);
      socket.disconnect();
    };
  }, [ticketingId, joinRoom, debouncedOnUpdated]);
};
